// 픽셀 아트 격자: 그림 데이터와 그리기 알고리즘만 모은 순수 모듈.
// React·DOM에 기대는 곳은 paintGrid / renderPngBlob / localStorage 세 군데뿐이다.

export const COLS = 40;
export const ROWS = 40;
export const CELL_PX = 10;
export const CANVAS_PX = COLS * CELL_PX;
/** 내보내는 PNG는 화면보다 두 배 크게 만든다(장면에서 확대해도 덜 번지도록). */
export const EXPORT_CELL_PX = 20;

/** "#rrggbb"(소문자) 또는 투명(null). */
export type PixelColor = string | null;

export interface PixelGrid {
  readonly cols: number;
  readonly rows: number;
  /** index = y * cols + x */
  readonly cells: readonly PixelColor[];
}

/** 칸 좌표. 격자 밖의 값도 허용한다. */
export interface Point {
  x: number;
  y: number;
}

export type ShapeKind = "rect" | "ellipse";

export const createGrid = (cols = COLS, rows = ROWS): PixelGrid => ({
  cols,
  rows,
  cells: new Array<PixelColor>(cols * rows).fill(null),
});

export const gridEquals = (a: PixelGrid, b: PixelGrid): boolean => {
  if (a === b) {
    return true;
  }
  if (a.cols !== b.cols || a.rows !== b.rows) {
    return false;
  }
  for (let i = 0; i < a.cells.length; i++) {
    if (a.cells[i] !== b.cells[i]) {
      return false;
    }
  }
  return true;
};

export const isEmptyGrid = (grid: PixelGrid): boolean =>
  grid.cells.every((cell) => cell === null);

const inBounds = (grid: PixelGrid, x: number, y: number) =>
  Number.isInteger(x) &&
  Number.isInteger(y) &&
  x >= 0 &&
  y >= 0 &&
  x < grid.cols &&
  y < grid.rows;

/**
 * 격자를 바꾸는 유일한 길(불변). 격자 밖 칸은 무시하고,
 * 바뀐 칸이 없으면 같은 참조를 돌려준다.
 */
export const setCells = (
  grid: PixelGrid,
  points: readonly Point[],
  color: PixelColor,
): PixelGrid => {
  let next: PixelColor[] | null = null;
  for (const { x, y } of points) {
    if (!inBounds(grid, x, y)) {
      continue;
    }
    const index = y * grid.cols + x;
    if ((next ?? grid.cells)[index] === color) {
      continue;
    }
    next ??= grid.cells.slice();
    next[index] = color;
  }
  return next ? { cols: grid.cols, rows: grid.rows, cells: next } : grid;
};

export const mirrorPoint = (point: Point, cols: number): Point => ({
  x: cols - 1 - point.x,
  y: point.y,
});

const withMirror = (
  points: readonly Point[],
  cols: number,
  mirror: boolean,
): Point[] =>
  mirror
    ? [...points, ...points.map((point) => mirrorPoint(point, cols))]
    : points.slice();

/** Bresenham 직선. 양 끝 칸을 포함한다. */
export const linePoints = (a: Point, b: Point): Point[] => {
  if (
    ![a.x, a.y, b.x, b.y].every(Number.isFinite) ||
    ![a.x, a.y, b.x, b.y].every(Number.isInteger)
  ) {
    return [];
  }
  const points: Point[] = [];
  let x = a.x;
  let y = a.y;
  const dx = Math.abs(b.x - a.x);
  const dy = -Math.abs(b.y - a.y);
  const sx = a.x < b.x ? 1 : -1;
  const sy = a.y < b.y ? 1 : -1;
  let err = dx + dy;
  for (;;) {
    points.push({ x, y });
    if (x === b.x && y === b.y) {
      break;
    }
    const e2 = 2 * err;
    if (e2 >= dy) {
      err += dy;
      x += sx;
    }
    if (e2 <= dx) {
      err += dx;
      y += sy;
    }
  }
  return points;
};

/** 펜(색)과 지우개(null) 공통. */
export const paintLine = (
  grid: PixelGrid,
  from: Point,
  to: Point,
  color: PixelColor,
  mirror: boolean,
): PixelGrid =>
  setCells(grid, withMirror(linePoints(from, to), grid.cols, mirror), color);

/** 4방향 채우기. 대상 색과 칠할 색이 같으면 같은 참조를 돌려준다. */
export const floodFill = (
  grid: PixelGrid,
  at: Point,
  color: PixelColor,
  mirror: boolean,
): PixelGrid => {
  const fillOnce = (source: PixelGrid, start: Point): PixelGrid => {
    if (!inBounds(source, start.x, start.y)) {
      return source;
    }
    const { cols, rows } = source;
    const target = source.cells[start.y * cols + start.x];
    if (target === color) {
      return source;
    }
    const cells = source.cells.slice();
    const visited = new Uint8Array(cols * rows);
    const first = start.y * cols + start.x;
    const stack = [first];
    visited[first] = 1;
    while (stack.length > 0) {
      const index = stack.pop()!;
      cells[index] = color;
      const x = index % cols;
      const y = (index - x) / cols;
      const neighbours: [number, number][] = [
        [x - 1, y],
        [x + 1, y],
        [x, y - 1],
        [x, y + 1],
      ];
      for (const [nx, ny] of neighbours) {
        if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) {
          continue;
        }
        const next = ny * cols + nx;
        if (!visited[next] && cells[next] === target) {
          visited[next] = 1;
          stack.push(next);
        }
      }
    }
    return { cols, rows, cells };
  };

  const filled = fillOnce(grid, at);
  return mirror ? fillOnce(filled, mirrorPoint(at, grid.cols)) : filled;
};

const bounds = (a: Point, b: Point) => ({
  x0: Math.min(a.x, b.x),
  x1: Math.max(a.x, b.x),
  y0: Math.min(a.y, b.y),
  y1: Math.max(a.y, b.y),
});

/** 두 점을 모서리로 하는 네모의 테두리. 폭이나 높이가 1~2면 전부 채운다. */
export const rectOutlinePoints = (a: Point, b: Point): Point[] => {
  const { x0, x1, y0, y1 } = bounds(a, b);
  const thin = x1 - x0 < 2 || y1 - y0 < 2;
  const points: Point[] = [];
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      if (thin || x === x0 || x === x1 || y === y0 || y === y1) {
        points.push({ x, y });
      }
    }
  }
  return points;
};

/** 채운 타원의 경계 칸. 상하좌우 대칭이 보장된다. */
export const ellipseOutlinePoints = (a: Point, b: Point): Point[] => {
  const { x0, x1, y0, y1 } = bounds(a, b);
  const radiusX = (x1 - x0) / 2 + 0.5;
  const radiusY = (y1 - y0) / 2 + 0.5;
  const cx = (x0 + x1) / 2;
  const cy = (y0 + y1) / 2;
  const inside = (x: number, y: number) =>
    x >= x0 &&
    x <= x1 &&
    y >= y0 &&
    y <= y1 &&
    ((x - cx) / radiusX) ** 2 + ((y - cy) / radiusY) ** 2 <= 1 + 1e-9;
  const points: Point[] = [];
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      if (
        inside(x, y) &&
        (!inside(x - 1, y) ||
          !inside(x + 1, y) ||
          !inside(x, y - 1) ||
          !inside(x, y + 1))
      ) {
        points.push({ x, y });
      }
    }
  }
  return points;
};

export const drawShape = (
  grid: PixelGrid,
  kind: ShapeKind,
  a: Point,
  b: Point,
  color: PixelColor,
  mirror: boolean,
): PixelGrid => {
  const outline =
    kind === "rect" ? rectOutlinePoints(a, b) : ellipseOutlinePoints(a, b);
  return setCells(grid, withMirror(outline, grid.cols, mirror), color);
};

// ---------- 되돌리기 ----------

export const MAX_UNDO = 100;

export interface PixelHistory {
  readonly baseline: PixelGrid;
  readonly past: readonly PixelGrid[];
  readonly present: PixelGrid;
  readonly future: readonly PixelGrid[];
}

export const createHistory = (initial: PixelGrid): PixelHistory => ({
  baseline: initial,
  past: [],
  present: initial,
  future: [],
});

export const commit = (
  history: PixelHistory,
  next: PixelGrid,
): PixelHistory => {
  if (gridEquals(history.present, next)) {
    return history;
  }
  const past = [...history.past, history.present];
  return {
    baseline: history.baseline,
    past: past.length > MAX_UNDO ? past.slice(past.length - MAX_UNDO) : past,
    present: next,
    future: [],
  };
};

export const canUndo = (history: PixelHistory) => history.past.length > 0;
export const canRedo = (history: PixelHistory) => history.future.length > 0;

export const undo = (history: PixelHistory): PixelHistory => {
  if (!canUndo(history)) {
    return history;
  }
  return {
    baseline: history.baseline,
    past: history.past.slice(0, -1),
    present: history.past[history.past.length - 1],
    future: [history.present, ...history.future],
  };
};

export const redo = (history: PixelHistory): PixelHistory => {
  if (!canRedo(history)) {
    return history;
  }
  return {
    baseline: history.baseline,
    past: [...history.past, history.present],
    present: history.future[0],
    future: history.future.slice(1),
  };
};

/** 처음 열었을 때와 달라졌는가(닫을 때 확인 여부). */
export const isDirty = (history: PixelHistory): boolean =>
  !gridEquals(history.present, history.baseline);

// ---------- 그리기·내보내기 ----------

type FillRectContext = Pick<
  CanvasRenderingContext2D,
  "clearRect" | "fillRect" | "fillStyle"
>;

export const paintGrid = (
  ctx: FillRectContext,
  grid: PixelGrid,
  cell = CELL_PX,
): void => {
  ctx.clearRect(0, 0, grid.cols * cell, grid.rows * cell);
  for (let y = 0; y < grid.rows; y++) {
    for (let x = 0; x < grid.cols; x++) {
      const color = grid.cells[y * grid.cols + x];
      if (color !== null) {
        ctx.fillStyle = color;
        ctx.fillRect(x * cell, y * cell, cell, cell);
      }
    }
  }
};

/** 800×800 PNG. devicePixelRatio를 곱하지 않는다. */
export const renderPngBlob = (grid: PixelGrid): Promise<Blob> =>
  new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    canvas.width = grid.cols * EXPORT_CELL_PX;
    canvas.height = grid.rows * EXPORT_CELL_PX;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      reject(new Error("canvas 2d context unavailable"));
      return;
    }
    paintGrid(ctx, grid, EXPORT_CELL_PX);
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error("PNG encoding failed"));
      }
    }, "image/png");
  });

const hex2 = (value: number) => value.toString(16).padStart(2, "0");

/** 이미지(어떤 해상도든)를 칸 중심 샘플로 격자로 되돌린다. */
export const fromImageData = (
  image: {
    width: number;
    height: number;
    data: ArrayLike<number>;
  },
  cols: number,
  rows: number,
): PixelGrid => {
  const cells: PixelColor[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const px = Math.floor(((c + 0.5) * image.width) / cols);
      const py = Math.floor(((r + 0.5) * image.height) / rows);
      const i = (py * image.width + px) * 4;
      cells.push(
        image.data[i + 3] < 128
          ? null
          : `#${hex2(image.data[i])}${hex2(image.data[i + 1])}${hex2(
              image.data[i + 2],
            )}`,
      );
    }
  }
  return { cols, rows, cells };
};

// ---------- 색 ----------

export const PALETTE_16: readonly { color: string; name: string }[] = [
  { color: "#000000", name: "검정" },
  { color: "#5f574f", name: "진회색" },
  { color: "#c2c3c7", name: "회색" },
  { color: "#ffffff", name: "흰색" },
  { color: "#ff004d", name: "빨강" },
  { color: "#ffa300", name: "주황" },
  { color: "#ffec27", name: "노랑" },
  { color: "#00e436", name: "연두" },
  { color: "#008751", name: "초록" },
  { color: "#29adff", name: "하늘" },
  { color: "#1d2b53", name: "남색" },
  { color: "#7e2553", name: "자주" },
  { color: "#ff77a8", name: "분홍" },
  { color: "#ffccaa", name: "살구" },
  { color: "#ab5236", name: "갈색" },
  { color: "#83769c", name: "보라회색" },
];

export const RECENT_MAX = 4;

const RECENT_KEY = "canvaschool.pixelArt.recentColors.v1";

/** "#rrggbb"만 받는다. 소문자로 돌려주고, 그 밖에는 null. */
export const normalizeColor = (value: unknown): string | null =>
  typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value)
    ? value.toLowerCase()
    : null;

export const pushRecentColor = (
  list: readonly string[],
  color: PixelColor,
  max = RECENT_MAX,
): string[] => {
  const normalized = normalizeColor(color);
  if (!normalized) {
    return list.slice(0, max);
  }
  return [normalized, ...list.filter((item) => item !== normalized)].slice(
    0,
    max,
  );
};

export const loadRecentColors = (): string[] => {
  try {
    const parsed: unknown = JSON.parse(
      window.localStorage.getItem(RECENT_KEY) ?? "null",
    );
    if (!Array.isArray(parsed)) {
      return [];
    }
    const colors: string[] = [];
    for (const item of parsed) {
      const normalized = normalizeColor(item);
      if (normalized && !colors.includes(normalized)) {
        colors.push(normalized);
      }
    }
    return colors.slice(0, RECENT_MAX);
  } catch {
    return [];
  }
};

export const saveRecentColors = (list: readonly string[]): void => {
  try {
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch {
    // 저장이 막혀도 그림은 계속 그릴 수 있다.
  }
};
